import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps, Radio,
  Table, Typography, Alert, Space, Divider, Tag, Spin, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined,
  UploadOutlined, StarFilled, StarOutlined, CalculatorOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';

const { Text } = Typography;
const { Option } = Select;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (n, c) => (n === null || n === undefined || !isFinite(Number(n))
  ? '—' : Number(n).toFixed(c === undefined ? 2 : c).replace('.', ','));

const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;

// Uma leitura a cada metro de circunferencia; uma linha de medicao a cada 2 m
// de altura do tanque.
const exigidasDe = (dd) => {
  if (!dd) return 0;
  const circ = dd.circunferencia_m ? Number(dd.circunferencia_m)
    : (dd.diametro_m ? Math.PI * Number(dd.diametro_m) : null);
  return circ && circ > 0 ? Math.ceil(circ) : 0;
};
const linhasDe = (dd) => {
  if (!dd) return 0;
  const h = dd.tipo_tanque === 'vertical' ? dd.altura_m : dd.comprimento_m;
  return h && Number(h) > 0 ? Math.ceil(Number(h) / 2) : 0;
};
const ehCostadoNome = (nome) => String(nome || '').toLowerCase().indexOf('costado') >= 0;

// Nivel de referencia para deduzir a coluna de liquido: o maximo informado;
// sem ele, a propria altura (ou comprimento) do tanque.
const refDe = (dd) => {
  if (!dd) return null;
  const h = dd.altura_maxima_liquido_m;
  if (h !== null && h !== undefined && h !== '' && Number(h) > 0) return Number(h);
  const dim = dd.tipo_tanque === 'vertical' ? dd.altura_m : dd.comprimento_m;
  return (dim === null || dim === undefined || dim === '') ? null : Number(dim);
};

const ROTULO_PADRAO = {
  medidor_espessura: 'Medidor de espessura', manometro: 'Manômetro',
  manovacuometro: 'Manovacuômetro', termometro: 'Termômetro', incubadora: 'Incubadora'
};
const nomePadrao = (t) => ROTULO_PADRAO[t] ||
  String(t || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const MATERIAIS_NO_TOPO = ['ASTM A 36'];
const ordenarMateriais = (lista) => {
  const topo = [], resto = [];
  (lista || []).forEach((m) => (MATERIAIS_NO_TOPO.indexOf(m.nome) >= 0 ? topo : resto).push(m));
  return topo.concat(resto);
};

const APOIOS = [
  ['fundo apoiado', 'Fundo apoiado diretamente sobre a base'],
  ['pernas', 'Sobre pernas'],
  ['sapatas', 'Sobre sapatas'],
  ['pedestais', 'Sobre pedestais'],
  ['selas', 'Sobre selas']
];

const METODOS = [
  ['ultrassom', 'Ultrassom — medição de espessura por fora'],
  ['entrada', 'Entrada no tanque — inspeção visual interna'],
  ['entrada e ultrassom', 'Entrada no tanque e ultrassom']
];

const NORMAS = [
  ['nenhuma', 'Nenhuma norma específica (prazo de 1 ano)'],
  ['api653', 'API 653 — intervalo pela taxa de corrosão'],
  ['ibp9', 'IBP — Guia 9 de Inspeção de Tanques'],
  ['n2318', 'Petrobras N-2318'],
  ['outra', 'Outra norma']
];

// Itens do checklist externo. O interno visual so entra quando houve entrada.
const CHECK_EXTERNO = [
  ['cl_identificacao', 'Identificação / placa do tanque legível'],
  ['cl_pintura', 'Pintura e revestimento externo'],
  ['cl_corrosao_externa', 'Corrosão externa do costado'],
  ['cl_corrosao_teto', 'Corrosão no teto'],
  ['cl_fundo_chapa', 'Chapa de fundo e anel de reforço'],
  ['cl_costura_solda', 'Cordões de solda visíveis'],
  ['cl_deformacao', 'Deformações, amassados ou abaulamentos'],
  ['cl_assentamento', 'Assentamento e nivelamento'],
  ['cl_base_fundacao', 'Base e fundação'],
  ['cl_vazamento', 'Sinais de vazamento'],
  ['cl_respiro', 'Respiro desobstruído'],
  ['cl_valvula_alivio', 'Válvula de alívio e vácuo'],
  ['cl_indicador_nivel', 'Indicador de nível'],
  ['cl_aterramento', 'Sistema de aterramento'],
  ['cl_bacia_contencao', 'Bacia de contenção / dique'],
  ['cl_escada', 'Escada, plataforma e guarda-corpo'],
  ['cl_drenagem', 'Drenagem da bacia'],
  ['cl_bocas_visita', 'Bocas de visita e flanges'],
  ['cl_tubulacoes_conectadas', 'Tubulações conectadas e seus suportes'],
  ['cl_sinalizacao', 'Sinalização e identificação do produto']
];

const CHECK_INTERNO = [
  ['cli_costado', 'Superfície interna do costado'],
  ['cli_fundo', 'Superfície interna do fundo'],
  ['cli_teto', 'Superfície interna do teto'],
  ['cli_revestimento', 'Revestimento interno'],
  ['cli_residuo', 'Acúmulo de resíduo ou borra'],
  ['cli_pitting', 'Corrosão localizada (pitting)'],
  ['cli_soldas_internas', 'Soldas internas'],
  ['cli_acessorios_internos', 'Acessórios internos']
];

const OPC_CHECK = [['Conforme', 'Conforme'], ['Não conforme', 'Não conforme'],
  ['Não aplicável', 'N/A']];

export default function InspecaoTanque() {
  const nav = useNavigate();
  const { id } = useParams();

  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [regioes, setRegioes] = useState([]);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [materiais, setMateriais] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [padroes, setPadroes] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [imgs, setImgs] = useState({});
  const [croq, setCroq] = useState({});
  const [enviando, setEnviando] = useState(false);

  const set = (campo, v) => setD((x) => ({ ...x, [campo]: v }));

  const miniatura = useCallback(async (tipo, idx) => {
    try {
      const r = await api.get('/inspecoes-tanque/' + id + '/' + tipo + '/' + idx, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (tipo === 'fotos') setImgs((p) => ({ ...p, [idx]: url }));
      else setCroq((p) => ({ ...p, [idx]: url }));
    } catch (e) { /* miniatura fica vazia */ }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-tanque/' + id);
      const l = r.data;
      setRegioes(l.regioes || []);
      delete l.regioes;
      if (!l.status) l.status = 'rascunho';
      // junta sem radiografia e o criterio adotado pela Celum; editavel por laudo
      if (l.eficiencia_junta === null || l.eficiencia_junta === undefined) l.eficiencia_junta = 0.7;
      if (!l.capacidade_unidade) l.capacidade_unidade = 'm3';
      setD(l);
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i));
    } catch { message.error('Erro ao carregar o laudo'); }
  }, [id, miniatura]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/inspecoes-tanque/apoio/materiais').then((r) => setMateriais(r.data || [])).catch(() => {});
    api.get('/inspecoes-tanque/apoio/padroes').then((r) => setPadroes(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
  }, []);

  // Depois de salvar volta so o que o servidor calcula. Recarregar o laudo
  // inteiro passava por cima do que o usuario digitou enquanto o salvamento
  // estava em andamento.
  const recarregarCalculados = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-tanque/' + id);
      const l = r.data;
      setRegioes(l.regioes || []);
      setD((x) => ({ ...x,
        codigo: l.codigo, revisao: l.revisao, status: l.status || x.status,
        enquadra_nr13: l.enquadra_nr13, enquadra_motivo: l.enquadra_motivo,
        tipo_laudo: l.tipo_laudo, densidade: l.densidade,
        tensao_admissivel: l.tensao_admissivel, material: l.material,
        intervalo_calculado_anos: l.intervalo_calculado_anos,
        proxima_inspecao: l.proxima_inspecao, prazo_memoria: l.prazo_memoria,
        atualizado_em: l.atualizado_em, fotos: l.fotos, croquis: l.croquis }));
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i));
    } catch { /* mantem o que esta na tela */ }
  }, [id, miniatura]);

  const subir = (tipo) => async (file) => {
    const fd = new FormData(); fd.append('foto', file);
    setEnviando(true);
    try { await api.post('/inspecoes-tanque/' + id + '/' + tipo, fd); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao enviar a imagem'); }
    setEnviando(false);
    return false;
  };
  const remover = (tipo) => async (idx) => {
    try {
      await api.delete('/inspecoes-tanque/' + id + '/' + tipo + '/' + idx);
      if (tipo === 'fotos') setImgs({}); else setCroq({});
      await recarregarCalculados();
    } catch (e) { message.error('Erro ao remover'); }
  };
  const definirCapa = async (idx) => {
    try { await api.put('/inspecoes-tanque/' + id + '/fotos/' + idx + '/principal'); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  // ------------------------------------------------------------ regioes
  const novaRegiao = () => setRegioes((rs) => rs.concat([{
    ordem: rs.length, regiao: null, descricao: '', altura_liquido_m: null,
    espessura_nominal_mm: null, e1: null, e2: null, e3: null, e4: null,
    espessura_anterior_mm: null, espessura_minima_manual: false
  }]));
  const mudaRegiao = (i, campo, v) => setRegioes((rs) => {
    const n = rs.slice();
    n[i] = { ...n[i], [campo]: v };
    // escolher Costado ja abre a quantidade de leituras que a circunferencia pede
    // a coluna de liquido e preenchida no proprio campo, nao so sugerida
    if (campo === 'altura_medicao_m') {
      const ref = refDe(d);
      if (ref !== null && v !== null && v !== undefined && v !== '') {
        n[i].altura_liquido_m = Math.max(ref - Number(v), 0);
      }
    }
    if (campo === 'regiao' && ehCostadoNome(v)) {
      const alvo = exigidasDe(d);
      const atual = Array.isArray(n[i].leituras) ? n[i].leituras.slice() : [];
      while (atual.length < alvo) atual.push(null);
      n[i].leituras = atual;
    }
    return n;
  });

  const ajustarLeituras = (i) => setRegioes((rs) => {
    const n = rs.slice();
    const alvo = exigidasDe(d);
    const atual = Array.isArray(n[i].leituras) ? n[i].leituras.slice() : [];
    while (atual.length < alvo) atual.push(null);
    n[i] = { ...n[i], leituras: atual };
    return n;
  });

  const mudaLeitura = (i, k, v) => setRegioes((rs) => {
    const n = rs.slice();
    const lista = Array.isArray(n[i].leituras) ? n[i].leituras.slice() : [];
    lista[k] = v;
    n[i] = { ...n[i], leituras: lista };
    return n;
  });
  const removeRegiao = (i) => setRegioes((rs) => rs.filter((_, k) => k !== i));

  // ------------------------------------------------------------ salvar
  const salvar = async (aviso = true) => {
    if (!d) return null;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid',
        'razao_social', 'nome_cliente', 'cnpj', 'art_cliente', 'cnpj_faturamento',
        'os_numero', 'end_atend_logradouro', 'end_atend_numero', 'end_atend_bairro',
        'end_atend_cidade', 'end_atend_estado', 'end_atend_cep', 'fotos', 'croquis',
        'enquadra_nr13', 'enquadra_motivo', 'tipo_laudo', 'prazo_memoria']
        .forEach((k) => delete corpo[k]);
      if (d.enquadra_manual) {
        corpo.enquadra_nr13 = d.enquadra_nr13;
        corpo.enquadra_motivo = d.enquadra_motivo;
      }
      corpo.regioes = regioes.map((r, k) => ({ ...r, ordem: k }));
      await api.put('/inspecoes-tanque/' + id, corpo);
      await recarregarCalculados();
      naoSalvo.limpo();
      if (aviso) message.success('Laudo salvo');
      return true;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.erro || e.message));
      return null;
    } finally { setSalvando(false); }
  };

  const calcular = async () => {
    const ok = await salvar(false);
    if (!ok) return;
    try {
      const r = await api.post('/inspecoes-tanque/' + id + '/calcular');
      setAvisos(r.data.avisos || []);
      setRegioes(r.data.regioes_gravadas || []);
      if (r.data.laudo) setD((x) => ({ ...x, ...r.data.laudo }));
      message.success('Cálculo refeito');
    } catch (e) { message.error('Erro ao calcular'); }
  };

  const gerarPdf = async () => {
    message.loading({ content: 'Gerando o PDF...', key: 'pdf', duration: 0 });
    try {
      await salvar(false);
      const r = await api.get('/inspecoes-tanque/' + id + '/pdf', { responseType: 'blob' });
      message.destroy('pdf');
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      await recarregarCalculados();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) { /* resposta nao e json */ }
      message.error(msg);
    }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const vertical = d.tipo_tanque === 'vertical';
  // Prévia ao vivo: a tela nunca pode mostrar o enquadramento do salvamento
  // anterior enquanto o técnico ja mudou as medidas. O servidor refaz a conta
  // ao salvar; isto aqui so evita que a tela minta no meio do preenchimento.
  const diametroVivo = () => {
    const c = num(d.circunferencia_m);
    if (c !== null && c > 0) return c / Math.PI;
    return num(d.diametro_m);
  };
  const capacidadeCalculada = () => {
    const D = diametroVivo();
    const comp = num(vertical ? d.altura_m : d.comprimento_m);
    if (D === null || D <= 0 || comp === null || comp <= 0) return null;
    return Math.PI * Math.pow(D / 2, 2) * comp * 1000;
  };
  const capacidadeLitros = () => (d.capacidade_manual ? num(d.capacidade_l) : capacidadeCalculada());
  const capacidadeNaUnidade = () => {
    const litros = capacidadeLitros();
    if (litros === null) return null;
    return (d.capacidade_unidade || 'm3') === 'l' ? litros : litros / 1000;
  };
  const previaEnquadra = () => {
    if (d.enquadra_manual) {
      return { ok: d.enquadra_nr13 === true, txt: d.enquadra_motivo || 'Definido manualmente.' };
    }
    if (d.enterrado) {
      return { ok: false,
        txt: 'Tanque enterrado — excluído do escopo da NR-13 pelo item 13.2.2, alínea (n).' };
    }
    const fora = ['pernas', 'sapatas', 'pedestais', 'selas']
      .filter((a) => String(d.apoio || '').toLowerCase().indexOf(a) >= 0)[0];
    if (fora) {
      return { ok: false, txt: 'Tanque apoiado em ' + fora +
        ' — excluído do escopo da NR-13 pelo item 13.2.2, alínea (n).' };
    }
    const D = diametroVivo();
    const cap = capacidadeLitros();
    if (D === null || cap === null) {
      return { ok: null,
        txt: 'Informe a circunferência (ou o diâmetro), a altura e a forma de apoio.' };
    }
    const faltas = [];
    if (!(D > 3)) faltas.push('diâmetro externo de ' + fmt(D, 2) + ' m não excede 3 m');
    if (!(cap > 20000)) faltas.push('capacidade de ' + fmt(cap, 0) + ' L não excede 20.000 L');
    if (faltas.length) {
      return { ok: false, txt: 'Fora do escopo da NR-13: ' + faltas.join(' e ') +
        '. O item 13.2.1, alínea (f), exige as duas condições simultaneamente.' };
    }
    return { ok: true, txt: 'Enquadrado na NR-13: diâmetro externo de ' + fmt(D, 2) +
      ' m (acima de 3 m) e capacidade de ' + fmt(cap, 0) +
      ' L (acima de 20.000 L), conforme item 13.2.1, alínea (f).' };
  };
  const temEntrada = String(d.metodo_inspecao_interna || '').indexOf('entrada') >= 0;
  const prev = previaEnquadra();
  const titulo = prev.ok === false ? 'Inspeção - Tanque' : 'Inspeção NR13 - Tanque';

  const opcoesRegiao = vertical
    ? ['Costado - virola 1', 'Costado - virola 2', 'Costado - virola 3',
       'Costado - virola 4', 'Costado - virola 5', 'Costado - virola 6', 'Teto', 'Fundo']
    : ['Costado', 'Tampo 1', 'Tampo 2', 'Fundo'];

  const ehCostado = (nome) => String(nome || '').toLowerCase().indexOf('costado') >= 0;

  const simNao = (campo, rotulo, ajuda, span = 6) => (
    <Col span={span}>
      <label>{rotulo}</label>
      <Select style={{ width: '100%' }} allowClear placeholder="—"
        value={d[campo] === true ? true : d[campo] === false ? false : undefined}
        onChange={(v) => set(campo, v === undefined ? null : v)}
        options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} />
      {ajuda ? aj(ajuda) : null}
    </Col>
  );

  const check = (lista) => (
    <Row gutter={[12, 10]}>
      {lista.map(([campo, rotulo]) => (
        <Col span={12} key={campo}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: 13 }}>{rotulo}</span>
            <Radio.Group size="small" optionType="button" buttonStyle="solid"
              value={d[campo] || undefined} onChange={(e) => set(campo, e.target.value)}
              options={OPC_CHECK.map(([v, r]) => ({ value: v, label: r }))} />
          </div>
        </Col>
      ))}
    </Row>
  );

  const etapas = [{ title: 'Identificação' }, { title: 'Enquadramento' }, { title: 'O tanque' },
    { title: 'Método' }, { title: 'Medição' }, { title: 'Checklist' }, { title: 'Prazo' },
    { title: 'Fotos e croqui' }, { title: 'Resultado' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/laudos')}>Voltar</Button>
        <b>{titulo}</b>
        {d.codigo && <Tag color="blue">{d.codigo}</Tag>}
        {prev.ok === false && <Tag color="orange">Fora da NR-13</Tag>}
        {prev.ok === true && <Tag color="green">NR-13</Tag>}
      </Space>

      {avisos.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pontos de atenção no cálculo"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>
            {avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>} />
      )}

      <Steps current={step} size="small" items={etapas} onChange={setStep} style={{ marginBottom: 16 }} />

      {/* ---------------------------------------------- 0. ENQUADRAMENTO */}
      {step === 0 && <CabecalhoLaudo d={d} set={set} />}

      {step === 1 && (
        <Card title="Enquadramento na NR-13" size="small">
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="Estas respostas decidem o laudo"
            description={'A NR-13 se aplica a tanque com diâmetro externo acima de 3 m E capacidade acima de 20.000 L, '
              + 'pelo item 13.2.1, alínea (f). O item 13.2.2, alínea (n), exclui tanque enterrado e tanque apoiado '
              + 'em pernas, sapatas, pedestais ou selas. Quando o tanque fica fora, o título do laudo passa a ser '
              + 'apenas "Laudo de Inspeção", sem a citação da norma.'} />

          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Tipo de tanque</label>
              <Select style={{ width: '100%' }} value={d.tipo_tanque || undefined}
                onChange={(v) => set('tipo_tanque', v)} placeholder="—"
                options={[{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]} />
            </Col>
            <Col span={6}>
              <label>Tipo de teto</label>
              <Select style={{ width: '100%' }} allowClear value={d.teto_tipo || undefined}
                onChange={(v) => set('teto_tipo', v || null)} placeholder="—"
                options={[{ value: 'fixo', label: 'Fixo' }, { value: 'flutuante', label: 'Flutuante' },
                  { value: 'aberto', label: 'Aberto' }]} />
            </Col>
            <Col span={12}>
              <label>Forma de apoio</label>
              <Select style={{ width: '100%' }} value={d.apoio || undefined}
                onChange={(v) => set('apoio', v)} placeholder="—"
                options={APOIOS.map(([v, l]) => ({ value: v, label: l }))} />
              {aj('Apoio em pernas, sapatas, pedestais ou selas tira o tanque do escopo da NR-13.')}
            </Col>

            {simNao('enterrado', 'Tanque enterrado?', 'Tanque enterrado está fora do escopo da NR-13.')}
            <Col span={6}>
              <label>Circunferência (m)</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.circunferencia_m)} onChange={(v) => set('circunferencia_m', v)} />
              {aj(num(d.circunferencia_m)
                ? 'Diâmetro: ' + fmt(Number(d.circunferencia_m) / Math.PI, 3) + ' m'
                : 'Medida com trena ao redor do tanque. O diâmetro sai dela.')}
            </Col>
            <Col span={6}>
              <label>Diâmetro externo (m)</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                disabled={!!num(d.circunferencia_m)}
                value={num(d.diametro_m)} onChange={(v) => set('diametro_m', v)} />
              {aj(num(d.circunferencia_m)
                ? 'Vem da circunferência. Apague a circunferência para digitar o diâmetro.'
                : 'Use este campo só se mediu o diâmetro direto.')}
            </Col>
            <Col span={6}>
              <label>{vertical ? 'Altura (m)' : 'Comprimento (m)'}</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(vertical ? d.altura_m : d.comprimento_m)}
                onChange={(v) => set(vertical ? 'altura_m' : 'comprimento_m', v)} />
            </Col>
            <Col span={5}>
              <label>Capacidade</label>
              <InputNumber style={{ width: '100%' }} min={0} step={1} decimalSeparator=","
                value={capacidadeNaUnidade()}
                onChange={(v) => setD((x) => ({ ...x, capacidade_manual: true,
                  capacidade_l: v === null ? null
                    : ((x.capacidade_unidade || 'm3') === 'l' ? v : v * 1000) }))} />
              {aj(d.capacidade_manual ? 'Informada na mão.' : 'Calculada pela geometria do tanque.')}
            </Col>
            <Col span={3}>
              <label>Unidade</label>
              <Select style={{ width: '100%' }} value={d.capacidade_unidade || 'm3'}
                onChange={(v) => set('capacidade_unidade', v)}
                options={[{ value: 'm3', label: 'm³' }, { value: 'l', label: 'litros' }]} />
            </Col>
            <Col span={6}>
              <label>Origem da capacidade</label>
              <Select style={{ width: '100%' }} value={!!d.capacidade_manual}
                onChange={(v) => set('capacidade_manual', v)}
                options={[{ value: false, label: 'Calcular pela geometria' },
                  { value: true, label: 'Informar na mão' }]} />
              {aj('O enquadramento da NR-13 compara com 20.000 L, qualquer que seja a unidade.')}
            </Col>
          </Row>

          <Divider />
          <Alert
            type={prev.ok === true ? 'success' : prev.ok === false ? 'warning' : 'info'}
            showIcon
            message={prev.ok === true ? 'Enquadrado na NR-13'
              : prev.ok === false ? 'Fora do escopo da NR-13' : 'Enquadramento ainda não verificado'}
            description={<div>
              <div>{prev.txt}</div>
              <div style={{ marginTop: 6 }}><b>Título do laudo:</b> {titulo}</div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                Calculado com o que está na tela agora. É confirmado pelo servidor ao salvar.
              </div>
            </div>} />

          <Row gutter={16} style={{ marginTop: 16 }}>
            {simNao('enquadra_manual', 'Definir o enquadramento manualmente?',
              'Use só quando o responsável técnico quiser decidir contra o automático. O motivo passa a ser o que ele escrever.', 8)}
          </Row>
          {d.enquadra_manual && (
            <Row gutter={16} style={{ marginTop: 12 }}>
              {simNao('enquadra_nr13', 'Enquadra na NR-13?', null, 8)}
              <Col span={16}>
                <label>Justificativa que vai no laudo</label>
                <Input.TextArea rows={2} value={d.enquadra_motivo || ''}
                  onChange={(e) => set('enquadra_motivo', e.target.value)} />
              </Col>
            </Row>
          )}
        </Card>
      )}

      {/* ---------------------------------------------- 1. IDENTIFICACAO */}
      {step === 2 && (
        <Card title="O tanque" size="small">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <label>TAG / identificação</label>
                <Input value={d.tag || ''} onChange={(e) => set('tag', e.target.value)} />
              </Col>
              <Col span={6}>
                <label>Fabricante</label>
                <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} />
              </Col>
              <Col span={4}>
                <label>Ano de fabricação</label>
                <InputNumber style={{ width: '100%' }} min={1900} max={2100}
                  value={num(d.ano_fabricacao)} onChange={(v) => set('ano_fabricacao', v)} />
                {aj('Serve de base para a taxa de corrosão quando não há inspeção anterior.')}
              </Col>
              <Col span={4}>
                <label>Número de série</label>
                <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} />
              </Col>
              <Col span={4}>
                <label>Local de instalação</label>
                <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} />
              </Col>

              <Col span={6}>
                <label>Produto armazenado</label>
                <Select style={{ width: '100%' }} showSearch optionFilterProp="children"
                  value={d.fluido || undefined}
                  onChange={(v) => {
                    const f = fluidos.find((x) => x.nome === v);
                    setD((x) => ({ ...x, fluido: v,
                      classe_fluido: f ? f.classe : x.classe_fluido,
                      densidade: f && f.densidade !== null ? Number(f.densidade) : x.densidade }));
                  }}>
                  {fluidos.map((f) => (
                    <Option key={f.id} value={f.nome}>
                      {f.nome} (Classe {f.classe}{f.densidade !== null ? ', d=' + fmt(f.densidade, 3) : ''})
                    </Option>
                  ))}
                  <Option value="outro">Outro</Option>
                </Select>
              </Col>
              {d.fluido === 'outro' && (
                <Col span={6}>
                  <label>Qual produto</label>
                  <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} />
                </Col>
              )}
              <Col span={4}>
                <label>Classe do fluido</label>
                <Select style={{ width: '100%' }} allowClear value={d.classe_fluido || undefined}
                  onChange={(v) => set('classe_fluido', v || null)}
                  options={['A', 'B', 'C', 'D'].map((c) => ({ value: c, label: 'Classe ' + c }))} />
                {aj('Vem do cadastro do fluido, mas pode trocar.')}
              </Col>
              <Col span={4}>
                <label>Densidade do produto</label>
                <InputNumber style={{ width: '100%' }} min={0} max={30} step={0.01} decimalSeparator=","
                  value={num(d.densidade)} onChange={(v) => set('densidade', v)} />
                {aj('Relativa à água. Entra no cálculo da espessura do costado.')}
              </Col>
              <Col span={5}>
                <label>Altura máxima de líquido (m)</label>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                  value={num(d.altura_maxima_liquido_m)} onChange={(v) => set('altura_maxima_liquido_m', v)} />
                {aj('É a coluna que empurra a chapa. Use no cálculo da região do fundo.')}
              </Col>
              <Col span={5}>
                <label>Temperatura de operação (°C)</label>
                <InputNumber style={{ width: '100%' }} step={1} decimalSeparator=","
                  value={num(d.temperatura_operacao)} onChange={(v) => set('temperatura_operacao', v)} />
              </Col>

              <Col span={8}>
                <label>Material do costado</label>
                <Select style={{ width: '100%' }} showSearch optionFilterProp="children"
                  value={d.material_id || undefined}
                  onChange={(v) => {
                    const m = materiais.find((x) => x.id === v);
                    setD((x) => ({ ...x, material_id: v,
                      material: m ? m.nome : x.material,
                      tensao_admissivel: m ? Number(m.tensao_mpa) : x.tensao_admissivel }));
                  }}>
                  {ordenarMateriais(materiais).map((m) => (
                    <Option key={m.id} value={m.id}>
                      {m.nome} — {fmt(m.tensao_mpa, 1)} MPa{m.norma_confirmada ? ' (' + m.norma + ')' : ''}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={5}>
                <label>Tensão admissível (MPa)</label>
                <InputNumber style={{ width: '100%' }} min={0} step={1} decimalSeparator=","
                  value={num(d.tensao_admissivel)} onChange={(v) => set('tensao_admissivel', v)} />
                {aj(d.tensao_admissivel ? '≈ ' + fmt(Number(d.tensao_admissivel) / 0.0980665, 0) + ' kgf/cm²' : 'Vem do material, editável.')}
              </Col>
              <Col span={5}>
                <label>Eficiência da junta (E)</label>
                <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} decimalSeparator=","
                  value={num(d.eficiencia_junta)} onChange={(v) => set('eficiencia_junta', v)} />
                {aj('0,70 é o critério adotado para junta sem radiografia. Quanto menor o E, maior a '
                  + 'espessura exigida: 0,70 pede 43% mais chapa que 1,00.')}
              </Col>
              <Col span={6}>
                <label>Sobrespessura de corrosão de projeto (mm)</label>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                  value={num(d.sobrespessura_corrosao_mm)} onChange={(v) => set('sobrespessura_corrosao_mm', v)} />
                {aj('Dado do fabricante. Sem o documento de projeto, deixe em branco — o laudo imprime "não informada". '
                  + 'Não afeta a aprovação; quem julga é a sobrespessura atual, calculada na etapa de medição.')}
              </Col>
            </Row>
          </Card>
      )}

      {/* ---------------------------------------------- 2. METODO */}
      {step === 3 && (
        <Card title="Método da inspeção" size="small">
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="A inspeção é sempre interna e externa"
            description={'O que muda é o método da parte interna: entrando no tanque, ou medindo a espessura por '
              + 'ultrassom pelo lado de fora. Quando não houve entrada, o laudo não afirma achado visual interno — '
              + 'a avaliação interna se sustenta na medição de espessura.'} />
          <Row gutter={[16, 16]}>
            <Col span={10}>
              <label>Método da inspeção interna</label>
              <Select style={{ width: '100%' }} value={d.metodo_inspecao_interna || undefined}
                onChange={(v) => set('metodo_inspecao_interna', v)} placeholder="—"
                options={METODOS.map(([v, l]) => ({ value: v, label: l }))} />
            </Col>
            <Col span={6}>
              <label>Periodicidade</label>
              <Select style={{ width: '100%' }} value={d.tipo_inspecao_periodicidade || undefined}
                onChange={(v) => set('tipo_inspecao_periodicidade', v)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' },
                  { value: 'extraordinaria', label: 'Extraordinária' }]} />
            </Col>
            <Col span={8}>
              <label>Data da inspeção anterior</label>
              <Input type="date" value={(d.data_inspecao_anterior || '').slice(0, 10)}
                onChange={(e) => set('data_inspecao_anterior', e.target.value || null)} />
              {aj('Com ela e a espessura anterior de cada região, o sistema apura a taxa de corrosão real.')}
            </Col>

            <Col span={10}>
              <label>Medidor de espessura</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.medidor_modelo || undefined}
                onChange={(v) => {
                  const p = padroes.find((x) => x.identificacao === v);
                  setD((x) => ({ ...x, medidor_modelo: v,
                    medidor_serie: p ? p.certificado : x.medidor_serie,
                    medidor_padrao_tipo: p ? p.tipo : x.medidor_padrao_tipo }));
                }}>
                {padroes.map((p) => (
                  <Option key={p.identificacao} value={p.identificacao}>
                    {nomePadrao(p.tipo)} — {p.identificacao}{p.marca ? ' (' + p.marca + ')' : ''}
                  </Option>
                ))}
              </Select>
              {aj('Vem do cadastro de padrões. O certificado de calibração fica no portal do cliente.')}
            </Col>
            <Col span={7}>
              <label>Condição da superfície</label>
              <Input value={d.condicao_superficie || ''} onChange={(e) => set('condicao_superficie', e.target.value)} />
            </Col>
            <Col span={7}>
              <label>Local da medição</label>
              <Input value={d.local_medicao || ''} onChange={(e) => set('local_medicao', e.target.value)} />
              {aj('Orientação de onde se mediu, ex.: quadrante norte, a 1 m do fundo.')}
            </Col>
          </Row>

          <Divider orientation="left">Dispositivos</Divider>
          <Row gutter={[16, 16]}>
            {simNao('possui_respiro', 'Possui respiro?', null, 5)}
            <Col span={3}>
              <label>Quantos</label>
              <InputNumber style={{ width: '100%' }} min={0} value={num(d.qtd_respiros)}
                onChange={(v) => set('qtd_respiros', v)} />
            </Col>
            {simNao('possui_valvula_alivio', 'Válvula de alívio e vácuo?', null, 5)}
            <Col span={3}>
              <label>Quantas</label>
              <InputNumber style={{ width: '100%' }} min={0} value={num(d.qtd_valvulas_alivio)}
                onChange={(v) => set('qtd_valvulas_alivio', v)} />
            </Col>
            {simNao('possui_indicador_nivel', 'Indicador de nível?', null, 4)}
            {simNao('possui_aterramento', 'Aterramento?', null, 4)}
            {simNao('possui_bacia_contencao', 'Bacia de contenção?', null, 5)}
            {simNao('possui_escada', 'Escada e guarda-corpo?', null, 5)}
          </Row>
        </Card>
      )}

      {/* ---------------------------------------------- 3. MEDICAO */}
      {step === 4 && (
        <Card title="Medição de espessura" size="small"
          extra={<Space>
            <Button icon={<PlusOutlined />} onClick={novaRegiao}>Adicionar região</Button>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={calcular}>Calcular</Button>
          </Space>}>
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="Como a espessura mínima é obtida"
            description={'No costado vale o método do pé da API 650: t = 4,9 × D × (H − 0,3) × G ÷ (S × E), '
              + 'calculado duas vezes — com a densidade do produto e com água (G = 1,0, que é a condição do teste '
              + 'hidrostático). Vale a maior. No teto e no fundo não há coluna de líquido: ali vale o mínimo de '
              + 'retirada de serviço cadastrado, e sem cadastro a região fica sem avaliação em vez de reprovar '
              + 'por um número que não é o certo.'} />

          {linhasDe(d) > 0 && (
            <Alert style={{ marginBottom: 12 }}
              type={regioes.filter((r) => ehCostadoNome(r.regiao)).length >= linhasDe(d)
                ? 'success' : 'warning'}
              showIcon
              message={'Cobertura do costado: ' +
                regioes.filter((r) => ehCostadoNome(r.regiao)).length + ' de ' + linhasDe(d) +
                ' linha(s) de medição'}
              description={'Uma linha a cada 2 m de altura. Este tanque tem ' +
                fmt(d.tipo_tanque === 'vertical' ? d.altura_m : d.comprimento_m, 2) +
                ' m, portanto são necessárias ' + linhasDe(d) + ' linhas, cada uma com ' +
                exigidasDe(d) + ' leituras (uma a cada metro de circunferência).'} />
          )}

          {regioes.length === 0 && <Text type="secondary">Nenhuma região. Clique em Adicionar região.</Text>}

          {regioes.map((r, i) => (
            <Card key={i} size="small" style={{ marginBottom: 12 }}
              title={<Space>
                <span>{r.regiao || 'Região ' + (i + 1)}</span>
                {r.aprovado === true && <Tag color="green">Aprovado</Tag>}
                {r.aprovado === false && <Tag color="red">Reprovado</Tag>}
                {r.aprovado === null && <Tag>Não avaliado</Tag>}
              </Space>}
              extra={<Button danger size="small" icon={<DeleteOutlined />}
                onClick={() => removeRegiao(i)} />}>
              <Row gutter={[12, 12]}>
                <Col span={6}>
                  <label>Região</label>
                  <Select style={{ width: '100%' }} value={r.regiao || undefined}
                    onChange={(v) => mudaRegiao(i, 'regiao', v)} placeholder="—"
                    options={opcoesRegiao.map((o) => ({ value: o, label: o }))} />
                </Col>
                <Col span={6}>
                  <label>Onde foi medido</label>
                  <Input value={r.descricao || ''} onChange={(e) => mudaRegiao(i, 'descricao', e.target.value)} />
                </Col>
                {ehCostado(r.regiao) && (
                  <Col span={4}>
                    <label>Altura do ponto medido (m)</label>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                      value={num(r.altura_medicao_m)} onChange={(v) => mudaRegiao(i, 'altura_medicao_m', v)} />
                    {aj('Do chão até onde o medidor encostou.')}
                  </Col>
                )}
                {ehCostado(r.regiao) && (
                  <Col span={4}>
                    <label>Coluna de líquido (m)</label>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                      value={num(r.altura_liquido_m)} onChange={(v) => mudaRegiao(i, 'altura_liquido_m', v)} />
                    {aj(refDe(d) !== null
                      ? 'Preenchido com ' + fmt(refDe(d), 2) + ' m menos a altura do ponto. Pode editar.'
                      : 'Preencha a altura do tanque ou o nível máximo de líquido para o cálculo automático.')}
                  </Col>
                )}
                <Col span={4}>
                  <label>Espessura nominal (mm)</label>
                  <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                    value={num(r.espessura_nominal_mm)} onChange={(v) => mudaRegiao(i, 'espessura_nominal_mm', v)} />
                </Col>
                <Col span={4}>
                  <label>Espessura na anterior (mm)</label>
                  <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                    value={num(r.espessura_anterior_mm)} onChange={(v) => mudaRegiao(i, 'espessura_anterior_mm', v)} />
                </Col>

                <Col span={24}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', margin: '6px 0' }}>
                    <b style={{ fontSize: 13 }}>
                      Leituras de espessura (mm) — {(r.leituras || []).length} de {exigidasDe(d)} exigidas
                    </b>
                    <Space>
                      <Button size="small" onClick={() => ajustarLeituras(i)}>
                        Gerar {exigidasDe(d)} campos
                      </Button>
                      <Button size="small"
                        onClick={() => mudaRegiao(i, 'leituras', (r.leituras || []).concat([null]))}>
                        + 1
                      </Button>
                    </Space>
                  </div>
                  {(r.leituras || []).length === 0
                    ? aj('Escolha a região para o sistema abrir as leituras, ou clique em Gerar.')
                    : (
                      <Row gutter={[6, 6]}>
                        {(r.leituras || []).map((v, k) => (
                          <Col span={2} key={k}>
                            <InputNumber size="small" style={{ width: '100%' }} min={0} step={0.01}
                              decimalSeparator="," placeholder={String(k + 1)}
                              value={v === null || v === undefined ? null : Number(v)}
                              onChange={(nv) => mudaLeitura(i, k, nv)} />
                          </Col>
                        ))}
                      </Row>
                    )}
                </Col>
                <Col span={12}>
                  <label>Observação da região</label>
                  <Input value={r.observacao || ''} onChange={(e) => mudaRegiao(i, 'observacao', e.target.value)} />
                </Col>
              </Row>
            </Card>
          ))}

          {regioes.length > 0 && (
            <>
              <Divider orientation="left">Resultado do cálculo</Divider>
              <Table size="small" pagination={false} rowKey={(r, i) => i}
                dataSource={regioes}
                columns={[
                  { title: 'Região', dataIndex: 'regiao', width: 150, render: (v) => v || '—' },
                  { title: 'Onde foi medido', dataIndex: 'descricao', width: 160,
                    render: (v) => v || '—' },
                  { title: 'Leituras', dataIndex: 'qtd_leituras', width: 90, align: 'center',
                    render: (v) => (v === null || v === undefined ? '—'
                      : <span style={{ color: v < exigidasDe(d) ? '#a8071a' : '#237804' }}>
                          {v + ' / ' + exigidasDe(d)}</span>) },
                  { title: 'Menor medida', width: 110, align: 'right',
                    render: (_, r) => fmt(r.menor_mm, 2) + ' mm' },
                  { title: 'Mín. produto', width: 110, align: 'right',
                    render: (_, r) => fmt(r.esp_min_projeto_mm, 3) },
                  { title: 'Mín. água', width: 100, align: 'right',
                    render: (_, r) => fmt(r.esp_min_teste_mm, 3) },
                  { title: 'MÍNIMA EXIGIDA', width: 140, align: 'right',
                    render: (_, r) => <b style={{ color: '#1a3c5e' }}>{fmt(r.espessura_minima_mm, 3) + ' mm'}</b> },
                  { title: 'Governou', width: 150, render: (_, r) => r.governa || '—' },
                  { title: 'Sobra atual', width: 120, align: 'right',
                    render: (_, r) => (r.sobrespessura_atual_mm === null
                      || r.sobrespessura_atual_mm === undefined ? '—'
                      : <b style={{ color: Number(r.sobrespessura_atual_mm) > 0 ? '#237804' : '#a8071a' }}>
                          {fmt(r.sobrespessura_atual_mm, 3) + ' mm'}</b>) },
                  { title: 'Construção', width: 110, align: 'right',
                    render: (_, r) => fmt(r.esp_min_construcao_mm, 2) },
                  { title: 'Taxa (mm/ano)', width: 120, align: 'right',
                    render: (_, r) => fmt(r.taxa_corrosao_mm_ano, 4) },
                  { title: 'Vida (anos)', width: 100, align: 'right',
                    render: (_, r) => fmt(r.vida_remanescente_anos, 1) },
                  { title: '', width: 110, align: 'center',
                    render: (_, r) => (r.aprovado === true ? <Tag color="green">OK</Tag>
                      : r.aprovado === false ? <Tag color="red">Reprovado</Tag> : <Tag>—</Tag>) }
                ]} />
              {aj('"Sobra atual" é a sobrespessura de corrosão que ainda resta: a espessura medida menos a '
                + 'mínima exigida. Sai de medição, não de documento de projeto, e por isso existe mesmo quando '
                + 'o projeto não está disponível. Zero significa que a região chegou ao limite.')}
              {aj('"Construção" é a espessura de projeto (fórmula + sobrespessura, ou o mínimo da API 650). '
                + 'É referência e não reprova: um tanque usado não é condenado por não ter a espessura de fábrica.')}
            </>
          )}
        </Card>
      )}

      {/* ---------------------------------------------- 4. CHECKLIST */}
      {step === 5 && (
        <>
          <Card title="Inspeção externa" size="small">
            {check(CHECK_EXTERNO)}
            <div style={{ marginTop: 16 }}>
              <label>Observações da inspeção externa</label>
              <Input.TextArea rows={3} value={d.checklist_observacoes || ''}
                onChange={(e) => set('checklist_observacoes', e.target.value)} />
            </div>
          </Card>
          <Card title="Inspeção interna visual" size="small" style={{ marginTop: 12 }}>
            {!temEntrada ? (
              <Alert type="info" showIcon
                message="Não se aplica a este laudo"
                description={'O método da inspeção interna está como ultrassom, sem entrada no tanque. '
                  + 'Sem entrada não há achado visual interno a relatar — a avaliação interna fica pela medição '
                  + 'de espessura. Se houve entrada, troque o método na etapa Método.'} />
            ) : (
              <>
                {check(CHECK_INTERNO)}
                <div style={{ marginTop: 16 }}>
                  <label>Observações da inspeção interna</label>
                  <Input.TextArea rows={3} value={d.checklist_interno_observacoes || ''}
                    onChange={(e) => set('checklist_interno_observacoes', e.target.value)} />
                </div>
              </>
            )}
          </Card>
        </>
      )}

      {/* ---------------------------------------------- 5. PRAZO */}
      {step === 6 && (
        <Card title="Prazo até a próxima inspeção" size="small"
          extra={<Button type="primary" icon={<CalculatorOutlined />} onClick={calcular}>Calcular</Button>}>
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="A NR-13 não fixa prazo para tanque"
            description={'O item 13.7.3.2 remete aos códigos ou normas aplicáveis. Então o prazo vem da norma '
              + 'que este laudo adotar, e o laudo imprime qual foi. Sem norma aplicável, o prazo é de 1 ano.'} />
          <Row gutter={[16, 16]}>
            <Col span={10}>
              <label>Norma de periodicidade adotada</label>
              <Select style={{ width: '100%' }} value={d.norma_periodicidade || 'nenhuma'}
                onChange={(v) => set('norma_periodicidade', v)}
                options={NORMAS.map(([v, l]) => ({ value: v, label: l }))} />
            </Col>
            {d.norma_periodicidade === 'outra' && (
              <Col span={8}>
                <label>Qual norma</label>
                <Input value={d.norma_periodicidade_outra || ''}
                  onChange={(e) => set('norma_periodicidade_outra', e.target.value)} />
              </Col>
            )}

            {d.norma_periodicidade === 'api653' ? (
              <>
                <Col span={5}>
                  <label>Fator da vida remanescente</label>
                  <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} decimalSeparator=","
                    value={num(d.fator_vida_remanescente)} onChange={(v) => set('fator_vida_remanescente', v)} />
                  {aj('0,50 é escolha conservadora nossa, não citação de norma. Ajuste conforme a API 653.')}
                </Col>
                <Col span={5}>
                  <label>Teto do intervalo (anos)</label>
                  <InputNumber style={{ width: '100%' }} min={1} max={40} step={1} decimalSeparator=","
                    value={num(d.teto_intervalo_anos)} onChange={(v) => set('teto_intervalo_anos', v)} />
                  {aj('Limite máximo em anos, qualquer que seja a vida remanescente.')}
                </Col>
              </>
            ) : d.norma_periodicidade && d.norma_periodicidade !== 'nenhuma' ? (
              <Col span={6}>
                <label>Intervalo pela norma (anos)</label>
                <InputNumber style={{ width: '100%' }} min={0} max={40} step={1} decimalSeparator=","
                  value={num(d.intervalo_informado_anos)} onChange={(v) => set('intervalo_informado_anos', v)} />
                {d.norma_periodicidade === 'ibp9' && aj('Guia 9, para tanque acima de 10 m de diâmetro: '
                  + '5 anos para derivados leves, 8 para petróleo bruto, diesel e gasóleo, 10 para óleo '
                  + 'combustível e pesados.')}
              </Col>
            ) : null}
          </Row>

          <Divider />
          <Row gutter={16}>
            <Col span={8}>
              <label>Intervalo calculado (anos)</label>
              <InputNumber style={{ width: '100%' }} disabled={!d.intervalo_manual}
                step={0.5} decimalSeparator=","
                value={num(d.intervalo_calculado_anos)} onChange={(v) => set('intervalo_calculado_anos', v)} />
            </Col>
            <Col span={8}>
              <label>Data da próxima inspeção</label>
              <Input type="date" disabled={!d.intervalo_manual}
                value={(d.proxima_inspecao || '').slice(0, 10)}
                onChange={(e) => set('proxima_inspecao', e.target.value || null)} />
            </Col>
            {simNao('intervalo_manual', 'Definir o prazo manualmente?',
              'Libera os dois campos acima e para de recalcular.', 8)}
          </Row>

          {d.prazo_memoria && (
            <Alert type="success" showIcon style={{ marginTop: 16 }}
              message="Memória do prazo (vai impressa no laudo)" description={d.prazo_memoria} />
          )}
          {d.pior_vida_remanescente_anos !== undefined && d.pior_vida_remanescente_anos !== null && (
            <div style={{ marginTop: 8 }}>
              {aj('Vida remanescente da região mais crítica: '
                + fmt(d.pior_vida_remanescente_anos, 1) + ' anos.')}
            </div>
          )}
        </Card>
      )}

      {/* ---------------------------------------------- 6. FOTOS */}
      {step === 7 && (
        <>
          <Card title="Registro fotográfico" size="small"
            extra={<Upload beforeUpload={subir('fotos')} showUploadList={false} accept="image/*" multiple>
              <Button icon={<UploadOutlined />} loading={enviando}>Adicionar foto</Button>
            </Upload>}>
            <Row gutter={[12, 12]}>
              {(d.fotos || []).map((f, i) => (
                <Col span={6} key={i}>
                  <Card size="small" cover={imgs[i]
                    ? <img alt={f.nome} src={imgs[i]} style={{ height: 150, objectFit: 'cover' }} />
                    : <div style={{ height: 150, background: '#fafafa' }} />}
                    actions={[
                      <Button type="text" size="small" onClick={() => definirCapa(i)}
                        icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} />,
                      <Button type="text" size="small" danger icon={<DeleteOutlined />}
                        onClick={() => remover('fotos')(i)} />
                    ]}>
                    <Input size="small" placeholder="Legenda" defaultValue={f.legenda || ''}
                      onBlur={(e) => {
                        const novas = (d.fotos || []).slice();
                        novas[i] = { ...novas[i], legenda: e.target.value };
                        set('fotos', novas);
                      }} />
                  </Card>
                </Col>
              ))}
            </Row>
            {(d.fotos || []).length === 0 && <Text type="secondary">Nenhuma foto.</Text>}
          </Card>

          <Card title="Croqui do tanque" size="small" style={{ marginTop: 12 }}
            extra={<Upload beforeUpload={subir('croquis')} showUploadList={false} accept="image/*" multiple>
              <Button icon={<UploadOutlined />} loading={enviando}>Adicionar croqui</Button>
            </Upload>}>
            <Row gutter={[12, 12]}>
              {(d.croquis || []).map((f, i) => (
                <Col span={8} key={i}>
                  <Card size="small" cover={croq[i]
                    ? <img alt={f.nome} src={croq[i]} style={{ height: 180, objectFit: 'contain' }} />
                    : <div style={{ height: 180, background: '#fafafa' }} />}
                    actions={[
                      <Button type="text" size="small" danger icon={<DeleteOutlined />}
                        onClick={() => remover('croquis')(i)} />
                    ]}>
                    <Input size="small" placeholder="Legenda" defaultValue={f.legenda || ''}
                      onBlur={(e) => {
                        const novas = (d.croquis || []).slice();
                        novas[i] = { ...novas[i], legenda: e.target.value };
                        set('croquis', novas);
                      }} />
                  </Card>
                </Col>
              ))}
            </Row>
            {(d.croquis || []).length === 0 && <Text type="secondary">Nenhum croqui.</Text>}
          </Card>
        </>
      )}

      {/* ---------------------------------------------- 7. RESULTADO */}
      {step === 8 && (
        <Card title="Resultado e conclusão" size="small"
          extra={<Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Resultado</label>
              <Select style={{ width: '100%' }} allowClear value={d.resultado || undefined}
                onChange={(v) => set('resultado', v || null)}
                options={[{ value: 'aprovado', label: 'Aprovado' },
                  { value: 'aprovado com restricoes', label: 'Aprovado com restrições' },
                  { value: 'reprovado', label: 'Reprovado' }]} />
            </Col>
            <Col span={6}>
              <label>Status do laudo</label>
              <Select style={{ width: '100%' }} value={d.status || 'rascunho'}
                onChange={(v) => set('status', v)}
                options={[{ value: 'rascunho', label: 'Rascunho' },
                  { value: 'concluido', label: 'Concluído' }]} />
            </Col>
            <Col span={24}>
              <label>Observações</label>
              <Input.TextArea rows={3} value={d.observacoes || ''}
                onChange={(e) => set('observacoes', e.target.value)} />
            </Col>
            <Col span={24}>
              <label>Ação corretiva</label>
              <Input.TextArea rows={3} value={d.acao_corretiva || ''}
                onChange={(e) => set('acao_corretiva', e.target.value)} />
            </Col>
            <Col span={24}>
              <label>Conclusão</label>
              <Input.TextArea rows={4} value={d.conclusao || ''}
                onChange={(e) => set('conclusao', e.target.value)} />
            </Col>
          </Row>
        </Card>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
          <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próxima</Button>
        </Space>
        <Space>
          {step === etapas.length - 1 && (
            <Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>
            Salvar
          </Button>
        </Space>
      </div>
    </div>
  );
}
